import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";

// Aviso en revisión. Cada <Pendiente /> marca un dato aún no confirmado (responsable, contacto, base legal o datos del proveedor).
const Pendiente = ({ campo }) => <span data-testid={`aviso-pendiente-${campo}`} className="bg-[#FEF3C7] text-[#92400E] font-semibold px-1 rounded">Pendiente de confirmar</span>;

const Seccion = ({ n, titulo, children }) => (
  <section className="mt-7" data-testid={`aviso-seccion-${n}`}>
    <h2 className="font-heading text-xl font-bold text-[#1C2421]">{n}. {titulo}</h2>
    <div className="text-base text-[#4A5550] leading-relaxed mt-2 space-y-2">{children}</div>
  </section>
);

const Lista = ({ items }) => <ul className="list-disc pl-5 space-y-1">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>;

const CorreoPrivacidad = ({ id }) => (
  <a href="mailto:privacidad@aldiafacil.es" data-testid={`aviso-correo-privacidad-${id}`} className="font-semibold text-[#3A6B56] underline underline-offset-2 break-all">privacidad@aldiafacil.es</a>
);

export default function AvisoPrivacidad() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAF8F5] px-5 pb-16" data-testid="aviso-privacidad" style={{ paddingTop: "max(env(safe-area-inset-top), 1.25rem)" }}>
      <div className="max-w-2xl mx-auto">
        <button data-testid="aviso-volver" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} className="min-h-[44px] inline-flex items-center gap-2 text-[#3A6B56] font-semibold">
          <ArrowLeft size={20} /> Volver
        </button>
        <div className="mt-3 flex gap-3 items-start bg-[#FEF3C7] border border-[#FCD34D] rounded-2xl p-4" data-testid="aviso-borrador">
          <AlertTriangle size={22} className="text-[#92400E] shrink-0 mt-0.5" />
          <p className="text-base text-[#92400E]"><b>Aviso en revisión.</b> Este texto no es definitivo. Los puntos marcados como «Pendiente de confirmar» se completarán cuando estén confirmados.</p>
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C2421] tracking-tight mt-6">Aviso de privacidad</h1>
        <p className="text-base text-[#6E7A74] mt-2">AL DÍA Fácil · Última actualización: <Pendiente campo="fecha" /></p>

        <Seccion n={1} titulo="Quién es el responsable">
          <p data-testid="aviso-responsable">Responsable: Ángel Luis Camarillo González.</p>
          <p data-testid="aviso-nif">NIF: 05351888H.</p>
          <p data-testid="aviso-direccion">Dirección postal de contacto: [DIRECCIÓN POSTAL DE CONTACTO DEL RESPONSABLE]</p>
          <p>Correo electrónico de privacidad: <CorreoPrivacidad id="1" />.</p>
          <p>El titular de AL DÍA Fácil es el responsable del tratamiento. Emergent, el proveedor tecnológico de la aplicación, actúa como encargado del tratamiento conforme a su contrato de encargo de tratamiento de datos (DPA).</p>
        </Seccion>

        <Seccion n={2} titulo="Para qué usamos tus datos y con qué base legal">
          <div className="space-y-3" data-testid="aviso-bases-legales">
            {[
              ["Crear y mantener tu cuenta e iniciar sesión.",
                "Ejecución de la relación contractual o precontractual con el usuario, necesaria para crear, mantener y permitir el acceso a la cuenta."],
              ["Guardar y organizar tus documentos, asuntos, recordatorios, gastos, planes de ocio y reservas, personas, viviendas, vehículos, mascotas y contactos.",
                "Ejecución de la relación contractual o precontractual con el usuario, necesaria para prestar las funciones principales de AL DÍA Fácil."],
              ["Analizar con IA los documentos, tickets y correos que tú decides analizar, y proponerte cómo organizarlos.",
                "Consentimiento del usuario. El análisis con IA es opcional: con «Guardar sin análisis» no se utiliza IA. Puedes retirar tu consentimiento en cualquier momento eligiendo «Guardar sin análisis» en lo que añadas a partir de entonces; la retirada no afecta a los análisis ya realizados."],
              ["Responder a lo que preguntes al Agente.",
                "Ejecución de la relación contractual o precontractual cuando utilizas voluntariamente el Agente para consultar o gestionar la información guardada en AL DÍA Fácil."],
              ["Enviarte correos y avisos (verificación, recuperación de contraseña, recordatorios y resumen semanal).",
                "Ejecución de la relación contractual o precontractual para las comunicaciones necesarias para prestar el servicio, como la verificación, la recuperación de contraseña y los avisos solicitados. Consentimiento para las comunicaciones opcionales que puedes activar o desactivar voluntariamente."],
              ["Atender los comentarios que nos envíes durante la beta.",
                "Interés legítimo del responsable en detectar incidencias, evaluar el funcionamiento de la aplicación y mejorar el servicio durante la fase beta."],
              ["Medir de forma agregada el uso de la beta (por ejemplo, días de uso o funciones utilizadas) para mejorar la aplicación. No se analiza el contenido de tus documentos para esto.",
                "Interés legítimo del responsable en conocer de forma agregada cómo funciona y se utiliza AL DÍA Fácil para mejorar el servicio. No se utiliza para elaborar perfiles individuales."],
            ].map(([fin, base], i) => (
              <div key={i} data-testid={`aviso-finalidad-${i + 1}`}>
                <p className="text-[#1C2421]">{i + 1}. {fin}</p>
                <p><span className="font-semibold">Base legal:</span> {base}</p>
              </div>
            ))}
          </div>
        </Seccion>

        <Seccion n={3} titulo="Qué datos tratamos">
          <Lista items={[
            "Datos de cuenta: nombre, correo electrónico y contraseña (guardada cifrada de forma irreversible), o los datos básicos de tu cuenta de Google si entras con ella.",
            "Contenido que tú añades: documentos e imágenes, y la información extraída de ellos.",
            "AL DÍA Fácil no admite documentación clínica: los documentos médicos se rechazan sin guardarse.",
            "Gastos e importes, planes de ocio y reservas, viviendas, vehículos, mascotas, contactos, recordatorios y pagos o recordatorios periódicos.",
            "Correos que tú pegas en AL DÍA (asunto, remitente, fecha, texto y adjunto). AL DÍA no accede a tu buzón de correo.",
            "Datos de otras personas que tú añades (por ejemplo, familiares). Debes añadirlos solo si tienes motivo legítimo para ello.",
            "Conversaciones con el Agente.",
            "Datos técnicos: zona horaria, tipo de dispositivo y navegador, registro del dispositivo para avisos y fechas de uso.",
            "Comentarios de la beta y, si las adjuntas, capturas de pantalla.",
          ]} />
        </Seccion>

        <Seccion n={4} titulo="Proveedores tecnológicos">
          <Lista items={[
            "Emergent (encargado del tratamiento): alojamiento de la aplicación, base de datos, almacenamiento de archivos y envío de correos. Emergent cuenta con subencargados (subprocesadores) que le ayudan a prestar el servicio.",
            "Google (Gemini): lectura de documentos, tickets y correos cuando tú lo solicitas, y respuestas del Agente. Las peticiones llegan a Google a través de Emergent.",
            "Google: inicio de sesión, solo si eliges entrar con tu cuenta de Google.",
            "Servicios de notificaciones de Apple, Google o Microsoft, según tu dispositivo, para entregar los avisos que actives.",
          ]} />
          <p>No vendemos tus datos ni los compartimos con otros usuarios.</p>
          <p>El contrato de tratamiento de datos de Emergent establece que los datos personales no se utilizan para entrenar modelos de IA.</p>
          <p>Condiciones específicas aplicables al análisis con Gemini: <Pendiente campo="gemini-condiciones" />.</p>
          <p>Papel concreto de Google en la cadena de tratamiento del análisis con IA: <Pendiente campo="gemini-papel" />.</p>
          <p>Regiones en las que se realiza el tratamiento con Gemini: <Pendiente campo="gemini-regiones" />.</p>
        </Seccion>

        <Seccion n={5} titulo="Dónde se tratan los datos">
          <p>Los datos se guardan en los servidores de nuestro proveedor tecnológico, Emergent.</p>
          <p>Según ha confirmado Emergent, el tratamiento puede realizarse en Estados Unidos, la Unión Europea e India.</p>
          <p>Para las transferencias internacionales, Emergent aplica los mecanismos contractuales correspondientes, incluidas las Cláusulas Contractuales Tipo de la Comisión Europea y, cuando procede, el complemento del Reino Unido.</p>
        </Seccion>

        <Seccion n={6} titulo="Seguridad">
          <p>Los datos están cifrados en tránsito y en reposo.</p>
          <p>Las contraseñas se guardan cifradas de forma irreversible y solo tú puedes ver tu información dentro de la aplicación.</p>
        </Seccion>

        <Seccion n={7} titulo="Cuánto tiempo los conservamos">
          <Lista items={[
            "Mientras mantengas tu cuenta activa.",
            "Si eliminas tu cuenta, borramos de nuestra base de datos la información asociada a ella. Los comentarios de la beta se conservan sin nombre, correo ni identificadores.",
            "Si en fases anteriores guardaste datos de salud, ya no se usan en la aplicación. Puedes descargarlos o eliminarlos desde Perfil.",
            <>Cuando se elimina una cuenta en Emergent, existe un proceso de eliminación que incluye la purga posterior de las copias de seguridad. Plazos concretos de ese proceso: <Pendiente campo="plazos-eliminacion" />.</>,
            <>Conservación de los datos enviados a Gemini (documentos, textos y resultados): <Pendiente campo="gemini" />.</>,
            <>Otras retenciones técnicas relacionadas con la IA: <Pendiente campo="ia-retencion" />.</>,
          ]} />
          <p className="font-semibold text-[#1C2421] pt-2">Copias de seguridad de la base de datos</p>
          <Lista items={[
            "Cada 6 horas y diarias: se conservan 7 días.",
            "Semanales: 4 semanas.",
            "Mensuales: 12 meses.",
            "Anuales: 1 año.",
            "Recuperación a un momento concreto: hasta 7 días.",
            "Las copias de seguridad no permiten borrar de forma selectiva un único dato o archivo. Los datos eliminados desaparecen de las copias cuando estas caducan.",
          ]} />
          <p className="font-semibold text-[#1C2421] pt-2">Archivos que subes</p>
          <Lista items={[
            "Los archivos originales (fotos y PDF) se guardan separados de la base de datos.",
            "El almacenamiento de archivos no tiene papelera ni guarda versiones anteriores.",
            "Cuando eliminas un documento o tu cuenta, el archivo deja de estar accesible desde AL DÍA. La aplicación no puede borrar cada archivo de forma individual: su eliminación física requiere actualmente la intervención del equipo de Soporte de Emergent.",
          ]} />
        </Seccion>

        <Seccion n={8} titulo="Tus derechos">
          <p>Puedes solicitar el acceso, la rectificación, la supresión, la limitación, la oposición y la portabilidad de tus datos, y retirar tu consentimiento en cualquier momento.</p>
          <p>Si consideras que no hemos atendido bien tu solicitud, puedes reclamar ante la Agencia Española de Protección de Datos (www.aepd.es).</p>
        </Seccion>

        <Seccion n={9} titulo="Cómo ejercerlos">
          <Lista items={[
            "Rectificar: puedes corregir la mayoría de tus datos directamente en la aplicación.",
            "Eliminar: desde Perfil › Eliminar cuenta.",
            "Avisos y notificaciones: puedes desactivarlos en Perfil o en los ajustes de tu móvil.",
            <>Acceso, rectificación, supresión, copia de tus datos u otras solicitudes: escríbenos desde el correo de tu cuenta a <CorreoPrivacidad id="2" />.</>,
          ]} />
        </Seccion>

        <Seccion n={10} titulo="Contacto">
          <p>Para cualquier duda sobre privacidad: <CorreoPrivacidad id="3" />.</p>
        </Seccion>
      </div>
    </div>
  );
}
